/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */

import { Button, Center, Group, Loader, useMantineTheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconAdjustmentsAlt, IconArrowsExchange, IconDownload, IconExclamationCircleFilled, IconSettingsX, IconUpload } from '@tabler/icons-react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Batch, BatchTypeEnum, EORIGIN_DATE_FORMAT_WITH_TIME, IBatchesCriteria, TemplateType } from '@e-origin/shared';

import { getInitialBatchCriteria, prefetchAdminBatches, useAdminBatches, useAdminExportBatch, useBatchCriteriaUrlSync } from '@admin/hooks';
import { SearchBox, Select, Table, TableActions, TableColumnProps } from '@components';
import { BATCH_LIST_NEXT_PAGE } from '@constants';
import { BatchView } from '@features/batches';
import { ContentLayout } from '@features/layout';
import { removeUndefined } from '@utils';
import { ChangeBatchStatus } from './ChangeBatchStatus';
import { ChangeStatusDeclarations } from './ChangeStatusDeclarations';
import { ImportBatch } from './ImportBatch';
import { IntegrityMissingLrns } from './IntegrityMissingLrns';
import { ReassignDeclarations } from './ReassignDeclarations';
import { ResetCustomsReport } from './ResetCustomsReport';

export const ManageBatches = () => {
  const theme = useMantineTheme();
  const exportBatchMutation = useAdminExportBatch();
  const [searchParams] = useSearchParams();
  const initialCriteria = getInitialBatchCriteria(searchParams);

  const [filters, setFilters] = useState<IBatchesCriteria['filters']>(initialCriteria.filters);
  const [sorting, setSorting] = useState<IBatchesCriteria['sorting']>(initialCriteria.sorting);
  const [pagination, setPagination] = useState<IBatchesCriteria['pagination']>(initialCriteria.pagination);
  useBatchCriteriaUrlSync({ filters, sorting, pagination });

  const [batchesData, setBatchesData] = useState<Batch[]>([]);
  const [batchView, setBatchView] = useState<BatchTypeEnum | undefined>();

  const { data: { list: batchesList, total: totalBatchCount } = { list: [], total: 0 }, isLoading } = useAdminBatches({ filters, sorting, pagination });

  const setBatchesFilters = (updatedFilters: Partial<IBatchesCriteria['filters']>) => {
    const mergedFilters = removeUndefined({
      ...filters,
      ...updatedFilters,
    });
    setFilters(mergedFilters);
  };

  useEffect(() => {
    if (!batchesList.length && !batchesData.length) {
      return;
    }

    setBatchesData(batchesList);
  }, [batchesList]);

  const prefetchNextPage = (page: number) => prefetchAdminBatches({ filters, sorting, pagination: { ...pagination, page } });

  const loadBatches = (batchViewType?: BatchTypeEnum) => {
    const tableView = batchViewType ?? batchView;
    let batchType: TemplateType;
    if ([BatchTypeEnum.HIGH_VALUES, BatchTypeEnum.HIGH_VALUES_EXPORT].includes(tableView)) {
      batchType = TemplateType.HIGH_VALUE_H1;
    }
    if (tableView === BatchTypeEnum.LOW_VALUES) {
      batchType = TemplateType.LOW_VALUE_H7;
    }

    setBatchesFilters({
      isExport: tableView === BatchTypeEnum.HIGH_VALUES_EXPORT,
      templateType: batchType,
    });
  };

  const searchBatches = (searchQuery: string) => {
    setBatchesFilters({ searchQuery });
    setPagination({ page: 1, size: pagination.size });
    prefetchNextPage(BATCH_LIST_NEXT_PAGE);
  };

  const handlePagination = (page: number, size: number) => {
    setPagination({ page, size });
    prefetchNextPage(page + 1);
  };

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination({ page: 1, size: pagination.size });
    prefetchNextPage(BATCH_LIST_NEXT_PAGE);
  };

  const openChangeBatchStatusModal = (batch: Batch) => {
    modals.open({
      title: 'Change Batch Status',
      children: <ChangeBatchStatus onClose={() => modals.closeAll()} batch={batch} />,
      size: 'lg',
      centered: true,
    });
  };

  const openChangeStatusDeclarationsModal = (batch: Batch) => {
    modals.open({
      title: 'Change Declarations Status',
      children: <ChangeStatusDeclarations onClose={() => modals.closeAll()} batchCounter={batch.counter} />,
      size: 'lg',
      centered: true,
    });
  };

  const openReassignDeclarations = (batch: Batch) => {
    modals.open({
      title: 'Reassign Declarations',
      children: <ReassignDeclarations onClose={() => modals.closeAll()} batchCounter={batch.counter} />,
      size: 'lg',
      centered: true,
    });
  };

  const openResetCustomsReport = (batch: Batch) => {
    modals.open({
      title: 'Reset customs report',
      children: <ResetCustomsReport onClose={() => modals.closeAll()} batch={batch} />,
      size: 'lg',
      centered: true,
    });
  };

  const openImportBatchModal = () => {
    let modalTitle = 'Import Batch';

    if (batchView) {
      switch (batchView) {
        case BatchTypeEnum.HIGH_VALUES:
          modalTitle = 'Import High Values Batch';
          break;
        case BatchTypeEnum.HIGH_VALUES_EXPORT:
          modalTitle = 'Import High Values Export Batch';
          break;
        case BatchTypeEnum.LOW_VALUES:
          modalTitle = 'Import Low Values Batch';
          break;
        default:
          modalTitle = 'Import Batch';
      }
    }

    modals.open({
      title: modalTitle,
      children: <ImportBatch onClose={() => modals.closeAll()} batchType={batchView} />,
      size: 'lg',
      centered: true,
    });
  };

  const openMissingSequence1Modal = (batch: Batch) => {
    const missingLrns = (batch.virtuals?.missingSequence1Lrns as unknown as string[]) || [];

    modals.open({
      title: `Missing Sequence 1 LRNs - Batch ${batch.counter}`,
      children: <IntegrityMissingLrns missingLrns={missingLrns} />,
      size: 'lg',
      centered: true,
    });
  };

  const columns: TableColumnProps<Batch>[] = [
    {
      hide: !filters.integrityCheckDays,
      name: 'Integrity issues',
      cell: (row) => (
        <Group gap="xs">
          <Button
            variant="subtle"
            size="xs"
            color="red"
            onClick={() => openMissingSequence1Modal(row)}
            leftSection={<IconExclamationCircleFilled color={theme.colors.red[6]} />}
          >
            View more
          </Button>
        </Group>
      ),
    },
    {
      name: 'Batch ID',
      selector: 'counter',
      width: '120px',
      cell: (row) => row.counter,
      sortable: true,
    },
    {
      name: 'Batch name',
      selector: 'name',
      sortable: true,
      cell: (row) => row.name,
    },
    {
      name: 'File name',
      selector: 'filename',
      cell: (row) => row.filename,
      sortable: true,
    },
    {
      name: 'Date',
      cell: (row) => moment(row.updatedAt).format(EORIGIN_DATE_FORMAT_WITH_TIME),
      selector: 'updatedAt',
      sortable: true,
    },
    {
      name: 'Template name',
      selector: 'template.name',
      cell: (row) => (row.template as any)?.name,
    },
    {
      name: 'Template version',
      selector: 'template.version',
      cell: (row) => (row.template as any)?.version,
    },
    {
      name: 'Template _id',
      selector: 'template._id',
      cell: (row) => (row.template as any)?._id,
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row) => {
        const secondaryActionsList = [
          {
            iconComponent: <IconAdjustmentsAlt color={theme.colors[theme.primaryColor][6]} />,
            text: 'Change Batch Status',
            onClick: () => openChangeBatchStatusModal(row),
          },
          {
            iconComponent: <IconAdjustmentsAlt color={theme.colors[theme.primaryColor][6]} />,
            text: 'Change Declarations Status',
            onClick: () => openChangeStatusDeclarationsModal(row),
          },
          {
            iconComponent: <IconArrowsExchange color={theme.colors[theme.primaryColor][6]} />,
            text: 'Reassign Declarations',
            onClick: () => openReassignDeclarations(row),
          },
          {
            if: !!row.reports?.customsReport,
            iconComponent: <IconSettingsX color={theme.colors[theme.primaryColor][6]} />,
            text: 'Reset customs report',
            onClick: () => openResetCustomsReport(row),
          },
          {
            iconComponent: <IconDownload color={theme.colors[theme.primaryColor][6]} />,
            text: 'Export Batch',
            onClick: () => exportBatchMutation.mutate(row._id),
            loading: exportBatchMutation.isPending,
          },
        ];

        return <TableActions actions={[]} secondaryActions={secondaryActionsList} showDebugMenu={false} batchView={batchView} batchdId={row._id} />;
      },
    },
  ];

  if (isLoading) {
    return (
      <Center style={{ height: '200px' }}>
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <ContentLayout>
      <ContentLayout.Header>
        <ContentLayout.Title>Manage Batches</ContentLayout.Title>
        <ContentLayout.Filters>
          <Button leftSection={<IconUpload size={16} />} onClick={openImportBatchModal}>
            Import Batch
          </Button>
          <BatchView
            value={batchView}
            onChange={(item) => {
              loadBatches(item);
              setBatchView(item);
            }}
          />
          <Select
            w="180px"
            label="Integrity Check"
            placeholder="Select period"
            clearable
            data={[
              { value: '1', label: '1 Day Before' },
              { value: '7', label: 'Past week' },
              { value: '30', label: 'Past month' },
              { value: '365', label: 'Past year' },
              { value: '0', label: 'No limit' },
            ]}
            value={filters?.integrityCheckDays?.toString()}
            onChange={(value) => {
              setBatchesFilters({
                integrityCheckDays: value ? parseInt(value) : undefined,
              });
              setPagination({ page: 1, size: pagination.size });
              prefetchNextPage(BATCH_LIST_NEXT_PAGE);
            }}
            disabled={!!filters.searchQuery || batchView === BatchTypeEnum.LOW_VALUES}
          />
          <SearchBox
            handleSearch={searchBatches}
            value={filters?.searchQuery}
            placeholder="Search by counter, name or file name"
            disabled={!!filters?.integrityCheckDays}
          />
        </ContentLayout.Filters>
      </ContentLayout.Header>
      <ContentLayout.Content>
        <Table
          columns={columns}
          dataSource={batchesData ?? []}
          total={totalBatchCount}
          onPaginationChange={handlePagination}
          onSortingChange={handleSorting}
          isLoading={isLoading}
          defaultPage={pagination.page}
          defaultPerPage={pagination.size}
          defaultSorting={sorting.field}
          defaultSortingDirection={sorting.direction}
        />
      </ContentLayout.Content>
    </ContentLayout>
  );
};
